/**
 * Public types for @reprotec/assistant-ui (Phase 65-03).
 *
 * Declared LOCALLY (not imported from @reprotec/assistant-core) so this stays a
 * pure frontend package with zero backend/domain coupling. The shapes mirror the
 * engine's AssistantAnswer but the package never imports app or engine code.
 */

/**
 * Descriptor the engine attaches to an answer when its result can be exported to
 * a Google Sheet. Opaque to the package: the host's exportToSheet transport is
 * the only thing that interprets reportType/params. Null (or absent) means the
 * answer is not exportable and no export affordance is shown.
 */
export interface AssistantExportable {
  reportType: 'job_costing' | 'labour_payroll' | 'materials_spend';
  params: { from?: string; to?: string };
}

/** The engine's answer shape, as returned by an app's /assistant/query (unwrapped). */
export interface AssistantAnswer {
  answer: string;
  toolsUsed: string[];
  /** Arbitrary source rows backing the answer; rendered generically. */
  data?: unknown;
  noTool?: boolean;
  /** When non-null, the answer can be exported to a Google Sheet (see AssistantPanelProps.exportToSheet). */
  exportable?: AssistantExportable | null;
}

/** One turn in the multi-turn thread sent back to the engine as context. */
export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** Typed failure the panel knows how to display. Transports throw this. */
export interface AssistantError {
  kind: 'rate_limited' | 'forbidden' | 'unknown';
  message: string;
}

/**
 * The only behaviour seam: the host app supplies a function that takes the user's
 * message + prior turns and resolves to an AssistantAnswer (or throws AssistantError).
 * The package hardcodes no endpoint, auth, tool, or column.
 */
export type AssistantTransport = (req: {
  message: string;
  recentTurns: ChatTurn[];
}) => Promise<AssistantAnswer>;

/**
 * Optional second seam: given an answer's exportable descriptor, produce a Google
 * Sheet URL. Host-supplied (hardcodes no endpoint/auth here). When omitted, the
 * panel shows no export affordance even if answers carry an exportable descriptor.
 */
export type AssistantExportToSheet = (exportable: AssistantExportable) => Promise<{ url: string }>;

export interface AssistantPanelProps {
  open: boolean;
  onClose: () => void;
  transport: AssistantTransport;
  /**
   * Optional export seam. When provided AND an answer carries a non-null
   * `exportable`, the panel renders an "Export to Google Sheet" button.
   */
  exportToSheet?: AssistantExportToSheet;
  /** Prompts shown in the empty state; clicking one submits it. */
  examplePrompts?: string[];
  /** Drawer header text. */
  title?: string;
  /** Host-supplied class on the drawer root for theming. */
  className?: string;
}

/** Type guard: is this an AssistantError (vs a generic Error)? */
export function isAssistantError(e: unknown): e is AssistantError {
  return (
    typeof e === 'object' &&
    e !== null &&
    'kind' in e &&
    'message' in e &&
    typeof (e as AssistantError).message === 'string'
  );
}
